import { useRef } from 'react';

/**
 * Сетка IPv4: 32 бита группами по 8, «бит над битом».
 *
 * rows: [{ label, bits?, dotted? }]
 * editableLabel — если задан, у этой строки ввод (bits из value)
 * highlight — индексы битов (0..31) для подсветки
 * value — строка до 32 символов (0/1/пробел) для редактируемой строки
 */
export default function TrainerIpBits({
  rows = [],
  editableLabel = null,
  value = '',
  onChange,
  highlight = [],
  locked = false,
}) {
  const inputRefs = useRef([]);
  const highlightSet = new Set(highlight || []);
  const bitsValue = String(value || '').padEnd(32, ' ').slice(0, 32);

  function setBit(index, digit) {
    if (locked || !onChange) return;
    const chars = bitsValue.split('');
    while (chars.length < 32) chars.push(' ');
    chars[index] = digit === '0' || digit === '1' ? digit : ' ';
    onChange(chars.join(''));
  }

  function handleKeyDown(e, index) {
    if (locked) return;
    if (e.key === '0' || e.key === '1') {
      e.preventDefault();
      setBit(index, e.key);
      if (index + 1 < 32) inputRefs.current[index + 1]?.focus();
      return;
    }
    if (e.key === 'Backspace') {
      e.preventDefault();
      const cur = bitsValue[index];
      if (cur === '0' || cur === '1') {
        setBit(index, ' ');
      } else if (index > 0) {
        setBit(index - 1, ' ');
        inputRefs.current[index - 1]?.focus();
      }
      return;
    }
    if (e.key === 'ArrowLeft' && index > 0) {
      e.preventDefault();
      inputRefs.current[index - 1]?.focus();
      return;
    }
    if (e.key === 'ArrowRight' && index < 31) {
      e.preventDefault();
      inputRefs.current[index + 1]?.focus();
    }
  }

  function renderBits(bits, { editable } = {}) {
    const raw = String(bits || '').padEnd(32, ' ').slice(0, 32);
    const octets = [];
    for (let o = 0; o < 4; o += 1) {
      const cells = [];
      for (let b = 0; b < 8; b += 1) {
        const i = o * 8 + b;
        const ch = raw[i];
        const isHi = highlightSet.has(i);
        if (editable) {
          cells.push(
            <input
              key={i}
              ref={(el) => {
                inputRefs.current[i] = el;
              }}
              type="text"
              inputMode="numeric"
              maxLength={1}
              className={[
                'trainer-ip-bit',
                'is-editable',
                isHi ? 'is-highlight' : '',
              ].filter(Boolean).join(' ')}
              value={ch === '0' || ch === '1' ? ch : ''}
              disabled={locked}
              onChange={() => {}}
              onKeyDown={(e) => handleKeyDown(e, i)}
              onFocus={(e) => e.target.select()}
              aria-label={`Бит ${i + 1}`}
            />,
          );
        } else {
          cells.push(
            <span
              key={i}
              className={[
                'trainer-ip-bit',
                isHi ? 'is-highlight' : '',
              ].filter(Boolean).join(' ')}
            >
              {ch === '0' || ch === '1' ? ch : '·'}
            </span>,
          );
        }
      }
      octets.push(
        <span key={o} className="trainer-ip-octet">
          {cells}
        </span>,
      );
    }
    return <div className="trainer-ip-bits-row">{octets}</div>;
  }

  return (
    <div className="trainer-ip-bits">
      {rows.map((row) => {
        const editable = Boolean(editableLabel) && row.label === editableLabel;
        return (
          <div key={row.label} className="trainer-ip-line">
            <span className="trainer-ip-label">{row.label}</span>
            <div className="trainer-ip-line-body">
              {renderBits(editable ? bitsValue : row.bits, { editable })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/** Десятичный блок: подписи + dotted.decimal */
export function TrainerIpDecimal({ rows = [] }) {
  return (
    <div className="trainer-ip-decimal">
      {rows.map((row) => (
        <div key={row.label} className="trainer-ip-line">
          <span className="trainer-ip-label">{row.label}</span>
          <code className="trainer-ip-dotted-only">{row.dotted || ''}</code>
        </div>
      ))}
    </div>
  );
}

/** Нормализация ответа сетки → 32 символа 0/1 или null */
export function normalizeIpBitsAnswer(raw) {
  const s = String(raw || '').replace(/[^01]/g, '');
  return s.length === 32 ? s : null;
}
